'use client';

import { Box } from '@wishket/design-system';
import { PropsWithChildren, RefObject } from 'react';
import { twMerge } from 'tailwind-merge';

import ArticleColumnToolbar from './ArticleColumnToolbar';
import ArticleRowToolbar from './ArticleRowToolbar';

import { ArticleToolbarProps } from '@/shared/types';
import {
  useDeviceCheck,
  useLocalStorageState,
  useRefInView,
  useScrollDirection,
} from '@/shared/hooks';
import { STORAGE_KEY } from '@/shared/constants';

interface ArticleResponsiveToolbarProps extends PropsWithChildren {
  toolbarProps: ArticleToolbarProps;
  commentListRef: RefObject<HTMLDivElement | null>;
}

const ArticleInteractiveToolbar = ({
  toolbarProps,
  commentListRef,
  children,
}: ArticleResponsiveToolbarProps) => {
  const { isDesktop } = useDeviceCheck();
  const [tutorialCompleted] = useLocalStorageState(STORAGE_KEY.TUTORIAL, false);
  const isFirstVisit = !tutorialCompleted;
  const { isScrollDown } = useScrollDirection();
  const isInView = useRefInView(commentListRef);
  const isBottomToolbarVisible = (isScrollDown && !isInView) || isFirstVisit;

  if (isDesktop)
    return (
      <Box
        id="side-toolbar-area"
        className={twMerge(
          'sticky top-[60px] transition',
          isInView ? 'hidden opacity-0' : 'top-[209px] opacity-100'
        )}
      >
        <ArticleColumnToolbar {...toolbarProps} />
        {children}
      </Box>
    );

  return (
    <>
      <Box
        id="bottom-toolbar-area"
        className={twMerge(
          'fixed bottom-0 left-0 z-30 flex h-[59px] w-[100vw] items-center border-t-[1px] border-t-gray-200 bg-white px-5 transition',
          isBottomToolbarVisible ? 'translate-y-0' : 'translate-y-full'
        )}
      >
        <ArticleRowToolbar {...toolbarProps} />
      </Box>
      {children}
    </>
  );
};

export default ArticleInteractiveToolbar;
